import React, { useEffect, useState } from "react";
import axios from "axios";
import "../assets/custom_styles.css";
import Factsheet_img from "../assets/img/Factsheet_img.png";
import Select from "react-select";

/*
 Factsheets Component
 - Provides UI to download a PDF factsheet either by District (population)
   or by Cancer Type.
 - Loads dropdown options for cancer types and districts via API calls.
 - Builds a payload based on user selections and requests a PDF blob,
   then triggers a client-side download.
*/
const Factsheets = () => {
  // Options for the top-level display selector: either show data by District
  // (population) or by Cancer type.
  const display = [
    { label: "District", value: "population" },
    { label: "Cancer", value: "Cancer" },
  ];
  const [selectedDisplay, setSelectedDisplay] = useState({
    // Default to population (district-focused factsheet)
    label: "Population",
    value: "population",
  });
  const [cancerList, setCancerList] = useState([]);
  const [selectedCancers, setSelectedCancers] = useState({
    // Default selection for cancer dropdown
    label: "All Cancers",
    value: "All",
  });
  const [districtList, setDistrictList] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState({
    // Default selection for district dropdown
    label: "All Districts",
    value: "All",
  });
  const [loading, setLoading] = useState(false);

  const handleDisplayChange = (val) => {
    setSelectedDisplay(val);

    if (val.value === "population") {
      // When switching to population view, hide/clear cancer selection
      setSelectedCancers(null);
    } else if (val.value === "Cancer") {
      // When switching to cancer view, hide/clear district selection
      setSelectedDistricts(null);
    }
  };

  const handleCancerChange = (val) => {
    setSelectedCancers(val);
  };

  const handleDistrictChange = (val) => {
    setSelectedDistricts(val);
  };

  useEffect(() => {
    // Fetch the available cancer types and districts for the Select controls.
    // This runs when component mounts and when selection state changes.
    const fetchData = async () => {
      try {
        const [cancerListRes, districtListRes] = await Promise.all([
          axios.get("Display_CancerList", { withCredentials: false }),
          axios.get("Display_DistrictList", { withCredentials: false }),
        ]);

        // Normalize the cancer list to the shape expected by react-select
        const allCancer = cancerListRes.data.cancer_list;
        const cancerType = allCancer.map((cancer) => ({
          id: cancer.cancer_master_id,
          label: `${cancer.cancer_code} - ${cancer.cancer_name}`,
          value: `${cancer.cancer_code}_${cancer.cancer_name}`,
          gender: cancer.gender,
        }));

        // Normalize the district list to the shape expected by react-select
        const allDistricts = districtListRes.data;
        const districts = allDistricts.map((district) => ({
          id: district.district_master_id,
          label: district.district_name,
          value: district.district_name,
        }));

        // Prepend an "All" option to both lists so users can request aggregated factsheets
        setCancerList([{ label: "All Cancers", value: "all" }, ...cancerType]);
        setDistrictList([
          { label: "All Districts", value: "all" },
          ...districts,
        ]);
      } catch (error) {
        // Log dropdown-loading errors but keep the UI responsive
        console.error("Dropdown fetch error:", error);
      }
    };
    fetchData();
  }, [selectedDisplay, selectedCancers, selectedDistricts]);
  const displayType = selectedDisplay?.value || "population";

  const fetchFactsheet = async () => {
    setLoading(true);
    const factsheetData = {
      // Build payload based on the selected view. If the user chose
      // population (district) view, include the district id; otherwise
      // set district to "all". For Cancer view, include the cancer id.
      display_type: displayType,
      district:
        displayType === "population" ? selectedDistricts?.id || "all" : "all",
      cancer_type:
        displayType === "Cancer" ? selectedCancers?.id || "all" : "all",
    };

    try {
      const token = sessionStorage.getItem("token");
      const response = await axios.post(
        "factSheetVisualization",
        factsheetData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Send token in headers
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      // The API returns a PDF blob. Create a temporary link and trigger
      // a client-side download without navigating away from the page.
      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      if (selectedDisplay.value === "population") {
        link.download = `Factsheet_${
          selectedDistricts?.value || "All_Districts"
        }_${selectedDisplay.label}.pdf`;
      } else if (selectedDisplay.value === "Cancer") {
        link.download = `Factsheet_${selectedCancers?.value || "All_Cancers"}_${
          selectedDisplay.label
        }.pdf`;
      }
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      // Handle download or API errors gracefully
      console.error("Factsheet fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-4">
      <div className="row">
        <div className="col-md-8 d-flex">
          <img
            className="img-fluid"
            style={{ width: "100%", maxWidth: "900px", height: "auto" }}
            src={Factsheet_img}
            alt="Factsheet"
          ></img>
        </div>

        <div className="col-md-4">
          <form>
            <div className="">
              <h3 className=" fs-4 my-3 text-primary">FactSheets</h3>
              <div className="mb-3">
                <label id="display" className="mb-2">
                  Display
                </label>
                <div>
                  <Select
                    type="checkbox"
                    aria-labelledby="display"
                    options={display}
                    value={selectedDisplay}
                    onChange={handleDisplayChange}
                    closeMenuOnSelect={true}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder="--Select--"
                  />
                </div>
              </div>
              {selectedDisplay.value === "Cancer" && (
                <div className="mb-3">
                  <label id="Cancer_type" className="mb-2">
                    Cancer Type
                  </label>
                  <div>
                    <Select
                      isMulti={false}
                      aria-labelledby="Cancer_type"
                      options={cancerList}
                      value={selectedCancers}
                      onChange={handleCancerChange}
                      closeMenuOnSelect={true}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="All Cancers"
                    />
                  </div>
                </div>
              )}
              {selectedDisplay.value === "population" && (
                <div className="mb-3">
                  <label id="district_name" className="mb-2">
                    Select District Name
                  </label>
                  <div>
                    <Select
                      isMulti={false}
                      aria-labelledby="district_name"
                      options={districtList}
                      value={selectedDistricts}
                      onChange={handleDistrictChange}
                      closeMenuOnSelect={true}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="All Districts"
                    />
                  </div>
                </div>
              )}
            </div>
          </form>

          <button
            type="button"
            className="btn btn-primary w-100 py-2"
            onClick={fetchFactsheet}
          >
            {loading ? (
              <div
                className="spinner-border spinner-border-sm text-light"
                role="status"
              >
                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              "Download Factsheet"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Factsheets;
